import { useMemo, useEffect, useState } from 'react'
import { createColumnHelper, ColumnDef } from '@tanstack/react-table'
import { DataTable } from 'components/DataTable'
import { OverflownTextTooltip } from 'components/OverflownTextTooltip'
import { useDataTableContext } from 'components/DataTable/DataTableContext'
import {
  filterStoreRepo,
  useFilterParams,
} from 'components/Filters/FilterStore'
import { FilterIdentifier } from 'models/saved_view'
import { TableSearch } from 'components/Filters/TableSearch'
import { dataTableSortingStoreRepo } from 'components/DataTable/DataTableSorting/DataTableSortingStore'
import VariantsFilterset from 'components/Tables/ProductTable/VariantsFilterset'
import { ColumnSelectorRecipient } from 'components/Modals/ColumnModal/ColumnModal'
import { FilterChips } from 'components/Filters/components/FilterChips/FilterChips'
import * as S from 'components/Tables/CommonTable.styles'
import TableButtonAction from 'components/Buttons/TableButtons/TableButtonAction'
import { LinkProductCombobox } from './LinkProductCombobox'
import { Link, Unlink, TriangleAlert } from 'lucide-react'
import { ActionButtons } from 'components/Buttons/ActionButtons'
import dialogConfirm from 'components/dialogConfirm'
import { notifySuccess } from 'services/toast'
import {
  useGetVariantsCatalog,
  useDeleteVariant,
  useBulkMatchVariants,
  type VariantCatalog,
} from 'services/products_api'
import { EditVariantModal } from './EditVariantModal'

export interface VariantSearchTableInnerProps {
  /**
   * Mode determines behavior:
   * - 'standalone': Used in variants view (for bulk export and bulk matching)
   * - 'selection': Used in product edit modal (for variant matching)
   */
  mode: 'standalone' | 'selection'

  /**
   * Unique key for the table (required for DataTable).
   */
  tableKey: string

  /**
   * Callback when selection changes (selection mode only).
   * Returns array of selected variant IDs.
   */
  onSelectionChange?: (variantIds: number[]) => void

  /**
   * If true, disable selection of variants matched to OTHER products (not current product).
   * Requires currentProductId to be set.
   */
  disableMatchedVariants?: boolean

  /**
   * The current product ID (used with disableMatchedVariants to determine which variants to grey out).
   */
  currentProductId?: number

  /**
   * Show search bar and filters (default: true for standalone, false for selection).
   */
  showFilters?: boolean

  /**
   * Initial selected variant IDs (for pre-selecting matched variants).
   */
  initialSelectedIds?: number[]

  /**
   * Callback when bulk match is triggered (standalone mode only).
   * Called with selected variant IDs and target product ID.
   */
  onBulkMatch?: (variantIds: number[], productId: number) => Promise<void>

  /**
   * Header content to display above the table (standalone mode only).
   * Typically includes title, description, and action buttons.
   */
  tableHeader?: React.JSX.Element

  /**
   * Footer content to display below the table (standalone mode only).
   * Typically includes export and bulk action buttons.
   */
  footerControls?: React.JSX.Element
}

const columnHelper = createColumnHelper<VariantCatalog>()

/**
 * Inner component that uses DataTable context.
 */
export function VariantSearchTableInner({
  mode,
  tableKey,
  onSelectionChange,
  disableMatchedVariants = false,
  currentProductId,
  showFilters = true,
  initialSelectedIds = [],
  tableHeader,
  footerControls,
}: VariantSearchTableInnerProps) {
  const VARIANTS_IDENTIFIER = `${tableKey}_VARIANTS` as FilterIdentifier

  // Get filter store for this table (includes pagination!)
  const variantsFilterStore = filterStoreRepo.getStore(VARIANTS_IDENTIFIER)
  const { page, setPage } = variantsFilterStore()
  const filtersAsParams = useFilterParams(variantsFilterStore)

  // Get sorting store for this table
  const { sorting } = dataTableSortingStoreRepo.getStore(tableKey)()

  const pageSize = 50 // Static page size

  // Edit/delete state
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedVariantId, setSelectedVariantId] = useState<
    number | undefined
  >()

  // Mutation hooks
  const deleteVariantMutation = useDeleteVariant()
  const bulkMatchMutation = useBulkMatchVariants()

  // Convert sorting to ordering string (e.g., [{id: 'name', desc: false}] => 'name' or '-name')
  const ordering = sorting?.[0]
    ? `${sorting[0].desc ? '-' : ''}${sorting[0].id}`
    : undefined

  // Fetch variants with lazy loading
  const { data, isLoading, error } = useGetVariantsCatalog({
    filters: filtersAsParams,
    page,
    page_size: pageSize,
    ordering,
  })

  // Get row selection from context
  const tableContext = useDataTableContext()
  const { rowSelection, isAllRowsSelected, totalSelectedRows } =
    tableContext?.state || {}
  const { setRowSelection, setTotalRowsInBackend, clearSelectedRows } =
    tableContext?.methods || {}

  // Pre-select rows based on initialSelectedIds
  useEffect(() => {
    if (
      mode === 'selection' &&
      data?.results &&
      initialSelectedIds.length > 0 &&
      setRowSelection
    ) {
      const initialSelection: Record<string, boolean> = {}
      data.results.forEach((variant, index) => {
        if (variant.id && initialSelectedIds.includes(variant.id)) {
          initialSelection[index.toString()] = true
        }
      })
      // Only set if there are matches
      if (Object.keys(initialSelection).length > 0) {
        console.log(
          'Pre-selecting variants:',
          initialSelection,
          'from IDs:',
          initialSelectedIds
        )
        setRowSelection(initialSelection)
      }
    }
  }, [mode, data?.results, initialSelectedIds, setRowSelection])

  // Notify parent of selection changes in selection mode
  useEffect(() => {
    if (
      mode === 'selection' &&
      onSelectionChange &&
      data?.results &&
      rowSelection
    ) {
      const selectedIds = Object.keys(rowSelection)
        .filter((key) => rowSelection[key])
        .map((key) => {
          const index = parseInt(key)
          return data.results[index]?.id
        })
        .filter((id): id is number => id !== undefined)

      onSelectionChange(selectedIds)
    }
  }, [mode, onSelectionChange, data?.results, rowSelection])

  // Update total rows for "select all" functionality
  useEffect(() => {
    if (data?.count && setTotalRowsInBackend) {
      setTotalRowsInBackend(data.count)
    }
  }, [data?.count, setTotalRowsInBackend])

  // Get selected variant IDs for bulk matching (standalone mode)
  const selectedVariantIds = useMemo(() => {
    if (!data?.results || !rowSelection) return []
    return Object.keys(rowSelection)
      .filter((key) => rowSelection[key])
      .map((key) => {
        const index = parseInt(key)
        return data.results[index]?.id
      })
      .filter((id): id is number => id !== undefined)
  }, [data?.results, rowSelection])

  // Get unselected variant IDs (for "select all" with exclusions)
  const unselectedVariantIds = useMemo(() => {
    if (!isAllRowsSelected || !data?.results) return []
    return Object.keys(rowSelection)
      .filter((key) => !rowSelection[key])
      .map((key) => {
        const index = parseInt(key)
        return data.results[index]?.id
      })
      .filter((id): id is number => id !== undefined)
  }, [data?.results, rowSelection, isAllRowsSelected])

  // Callback to clear selection after successful bulk link
  const handleLinkSuccess = () => {
    if (clearSelectedRows) {
      clearSelectedRows()
    }
  }

  // Handler for bulk unlink
  const handleUnlinkVariants = async () => {
    const count = totalSelectedRows || selectedVariantIds.length

    const confirmed = await dialogConfirm(
      `Are you sure you want to unlink ${count} variant${count !== 1 ? 's' : ''} from ${count !== 1 ? 'their' : 'its'} product?`,
      'Unlink Products'
    )

    if (!confirmed) return

    try {
      // If "select all" is active, use filter-based unlink mode
      const requestBody = isAllRowsSelected
        ? {
            unlink_mode: true,
            filters: filtersAsParams,
            exclude_ids: unselectedVariantIds,
          }
        : {
            // Explicit mode - unlink specific variants
            matches: selectedVariantIds.map((variantId) => ({
              variant_id: variantId,
              product_id: null,
            })),
          }

      const result = await bulkMatchMutation.mutateAsync(requestBody)

      // Success notification with actual count from server
      const matchedCount = result?.matched_count || count
      notifySuccess(
        `Successfully unlinked ${matchedCount} variant${matchedCount !== 1 ? 's' : ''}`
      )

      if (clearSelectedRows) {
        clearSelectedRows()
      }
    } catch (err) {
      console.error('Error unlinking variants:', err)
      // Error notification handled by mutation hook
    }
  }

  // Handler for edit button
  const handleRowEdit = (index: number) => {
    const variant = data?.results?.[index]
    if (!variant) return
    setSelectedVariantId(variant.id)
    setShowEditModal(true)
  }

  // Handler for delete button
  const handleRowDelete = async (index: number) => {
    const variant = data?.results?.[index]
    if (!variant?.id) return

    const confirmed = await dialogConfirm(
      `Are you sure you want to delete the variant "${variant.name}"?`,
      'Delete Variant'
    )

    if (!confirmed) return

    try {
      await deleteVariantMutation.mutateAsync(variant.id)
      // Success notification handled by mutation hook
    } catch (err) {
      console.error('Error deleting variant:', err)
      // Error notification handled by mutation hook
    }
  }

  const columns = useMemo<ColumnDef<VariantCatalog, any>[]>(() => {
    return [
      columnHelper.accessor('name', {
        header: () => '',
        enableHiding: false,
        meta: {
          headerTitle: 'Variant Name',
          isFirstColumn: true,
          isEditable: false,
        },
        cell: (info) => {
          const value = info.getValue() || '—'
          return (
            <OverflownTextTooltip tooltipText={value}>
              <div style={{ maxWidth: '250px' }}>{value}</div>
            </OverflownTextTooltip>
          )
        },
        size: 200,
        enableSorting: true,
      }),
      columnHelper.accessor('product_name', {
        header: 'Product',
        cell: (info) => {
          const productName = info.getValue()
          const productId = info.row.original.product_id
          const isUnmatched = !productName || !productId

          if (isUnmatched) {
            return (
              <div className="flex items-center gap-2 text-yellow-600">
                <TriangleAlert size={16} className="flex-shrink-0" />
                <span className="text-gray-400">No Product</span>
              </div>
            )
          }

          return productName
        },
        size: 180,
        enableSorting: true,
      }),
      columnHelper.accessor('sku', {
        header: 'SKU',
        cell: (info) => {
          const value = info.getValue() || '—'
          return (
            <OverflownTextTooltip tooltipText={value}>
              <div style={{ maxWidth: '150px' }}>{value}</div>
            </OverflownTextTooltip>
          )
        },
        size: 130,
        enableSorting: true,
      }),
      columnHelper.accessor('format_pack_size', {
        header: 'Format / pack size',
        cell: (info) => {
          const value = info.getValue() || '—'
          return (
            <OverflownTextTooltip tooltipText={value}>
              <div style={{ maxWidth: '150px' }}>{value}</div>
            </OverflownTextTooltip>
          )
        },
        size: 150,
        enableSorting: true,
      }),
      columnHelper.accessor('gtin', {
        header: 'GTIN / UPC',
        cell: (info) => {
          const value = info.getValue() || '—'
          return (
            <OverflownTextTooltip tooltipText={value}>
              <div style={{ maxWidth: '130px' }}>{value}</div>
            </OverflownTextTooltip>
          )
        },
        size: 120,
        enableSorting: true,
      }),
      columnHelper.accessor('avg_price', {
        header: 'Avg Price per Unit',
        cell: (info) => {
          const value = info.getValue()
          if (!value) return '—'
          // Format as currency
          return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
          }).format(value)
        },
        size: 110,
        enableSorting: true,
      }),
      {
        id: 'edit',
        header: 'Actions',
        size: 100,
        enableHiding: false,
        meta: { rightAlign: true, isLastColumn: true, isEditable: false },
        cell: ({ row }: any) => (
          <ActionButtons
            className="justify-end pr-0"
            onDelete={() => handleRowDelete(row.index)}
            onEdit={() => handleRowEdit(row.index)}
          />
        ),
      },
    ]
  }, [data?.results])

  if (error) {
    return (
      <div className="flex items-center justify-center h-64 text-red-600">
        Error loading variants: {error.message}
      </div>
    )
  }

  // Don't filter - show all variants but we'll grey out matched ones in the row config
  const tableData = useMemo(() => {
    if (!data?.results) return []
    return data.results
  }, [data?.results])

  // Disable selection for variants matched to OTHER products (not current product)
  const disabledRowsIdx = useMemo(() => {
    if (
      mode === 'selection' &&
      disableMatchedVariants &&
      data?.results &&
      currentProductId
    ) {
      return data.results
        .map((variant, idx) => {
          // Only disable if matched AND not matched to current product
          const isMatchedToOther =
            variant.match_status === 'matched' &&
            variant.product_id !== null &&
            variant.product_id !== currentProductId
          return isMatchedToOther ? idx : -1
        })
        .filter((idx) => idx !== -1)
    }
    return []
  }, [mode, disableMatchedVariants, data?.results, currentProductId])

  // Style variants matched to OTHER products as greyed out
  const rowAttributes = useMemo(() => {
    if (mode === 'selection' && disableMatchedVariants && currentProductId) {
      return (row: any) => {
        const isMatchedToOther =
          row.original.match_status === 'matched' &&
          row.original.product_id !== null &&
          row.original.product_id !== currentProductId
        if (isMatchedToOther) {
          return {
            className: 'opacity-50 pointer-events-none',
          } as any
        }
        return {} as any
      }
    }
    return undefined
  }, [mode, disableMatchedVariants, currentProductId])

  // Generate footer controls internally (for standalone mode with selection)
  const generatedFooterControls = useMemo(() => {
    if (mode !== 'standalone' || selectedVariantIds.length === 0) {
      return undefined
    }

    // TODO: Implement export functionality when backend endpoint is ready
    return (
      <div className="flex gap-2 items-center">
        <span className="text-sm text-gray-600">
          {selectedVariantIds.length} variant
          {selectedVariantIds.length !== 1 ? 's' : ''} selected
        </span>
      </div>
    )
  }, [mode, selectedVariantIds])

  return (
    <>
      <DataTable<VariantCatalog>
        tableKey={tableKey}
        tableHeader={
          <>
            {tableHeader}
            {showFilters && (
              <S.SearchContainer>
                <S.SearchContainerFilters>
                  <S.TableSearchContainer>
                    <TableSearch
                      filterStore={variantsFilterStore}
                      searchPlaceholder="Search by product name, variant name, SKU, or UPC/GTIN..."
                      tableKey={tableKey}
                    />
                  </S.TableSearchContainer>
                  <S.TableButtonsContainer>
                    <VariantsFilterset filterIdentifier={VARIANTS_IDENTIFIER} />
                    <ColumnSelectorRecipient tableKey={tableKey} />
                  </S.TableButtonsContainer>
                </S.SearchContainerFilters>
                <div className="flex flex-row justify-between gap-4">
                  {/* Bulk Actions (standalone mode only) */}
                  {mode === 'standalone' && (
                    <TableButtonAction
                      selectedRowsCount={
                        totalSelectedRows || selectedVariantIds.length
                      }
                      entityType="variants"
                      items={[
                        {
                          label: 'Link to Product',
                          icon: <Link size={20} />,
                          subContent: (
                            <LinkProductCombobox
                              selectedVariantIds={selectedVariantIds}
                              unselectedVariantIds={unselectedVariantIds}
                              totalSelectedRows={
                                totalSelectedRows || selectedVariantIds.length
                              }
                              isAllRowsSelected={isAllRowsSelected || false}
                              filters={filtersAsParams}
                              onSuccess={handleLinkSuccess}
                              buttonLess={true}
                            />
                          ),
                        },
                        {
                          label: 'Unlink Products',
                          icon: <Unlink size={20} />,
                          callback: handleUnlinkVariants,
                        },
                      ]}
                    />
                  )}
                  <FilterChips
                    identifier={VARIANTS_IDENTIFIER}
                    store={variantsFilterStore}
                    clearAllButton
                    showActive
                  />
                </div>
              </S.SearchContainer>
            )}
          </>
        }
        data={tableData}
        columns={columns}
        loading={isLoading}
        enableRowSelection={true}
        disabledRowsIdx={disabledRowsIdx}
        rowAttributes={rowAttributes}
        footerControls={footerControls || generatedFooterControls}
        sortableFields="all"
        isPaginationEnabled={true}
        selectAllText="Select Variants"
        paginationOptions={{
          isPaginationLoading: isLoading,
          setPage: setPage,
          page: page,
          totalRows: data?.count ?? 0,
          pageSize: pageSize,
        }}
      />

      {/* Edit Variant Modal */}
      {showEditModal && selectedVariantId && (
        <EditVariantModal
          show={showEditModal}
          variantId={selectedVariantId}
          onHide={() => {
            setShowEditModal(false)
            setSelectedVariantId(undefined)
          }}
          onSuccess={() => {
            // Query invalidation handled by mutation hook
          }}
        />
      )}
    </>
  )
}
